import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  UserOutlined, 
  LockOutlined, 
  BankOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined, 
  ExclamationCircleOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { Alert, Button, Card } from 'antd';
import {
  loginUser,
  setLoading,
  getRedirectAfterLogin,
  clearRedirectAfterLogin,
  hideSessionTimeout,
  hideSessionWarning,
  clearSessionTimeoutState,
  verifyAuth
} from '../slices/authSlice';
import apiService from '../services/apiService';

const ADMIN_HOME_PATH = '/admin/invoice-monitoring';
const ADMIN_ALLOWED_PATHS = ['/admin', '/users', '/audit'];

// Per-tab pre-timeout page (sessionStorage is unique per tab) for cross-tab re-login restoration:
// each tab restores to ITS OWN page, unlike the shared localStorage redirect that tabs overwrite.
const getPerTabRestorePath = () => { try { return sessionStorage.getItem('restorePathAfterRelogin'); } catch { return null; } };
const clearPerTabRestorePath = () => { try { sessionStorage.removeItem('restorePathAfterRelogin'); } catch { /* ignore */ } };

const normalizeLoginRedirect = (path, userRole) => {
  const fallback = userRole === 'admin' ? ADMIN_HOME_PATH : '/dashboard';
  if (!path || path === '/login' || path.startsWith('/login')) {
    return fallback;
  }

  const pathOnly = String(path).split(/[?#]/)[0];
  if (userRole === 'admin') {
    return ADMIN_ALLOWED_PATHS.some(prefix => pathOnly === prefix || pathOnly.startsWith(`${prefix}/`))
      ? path
      : ADMIN_HOME_PATH;
  }

  return ADMIN_ALLOWED_PATHS.some(prefix => pathOnly === prefix || pathOnly.startsWith(`${prefix}/`))
    ? '/dashboard'
    : path;
};

const LoginPage = ({ isMaintenanceMode = false, maintenanceData = null }) => {
  const [userType, setUserType] = useState('admin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [lastChecked, setLastChecked] = useState(new Date());
  const [isChecking, setIsChecking] = useState(false);
  const [bypassToken, setBypassToken] = useState('');
  const [showBypassInput, setShowBypassInput] = useState(false);
  const [bypassVerified, setBypassVerified] = useState(false);
  const [bypassError, setBypassError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  // TC-AUTH-008: State for concurrent session message
  const [concurrentSessionMessage, setConcurrentSessionMessage] = useState(null);
  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    username: '',
    password: '',
    companyCode: ''
  });
  const [touched, setTouched] = useState({
    username: false,
    password: false,
    companyCode: false
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, user } = useSelector((state) => state.auth);

  // Reset loading state when login page mounts
  useEffect(() => {
    clearSessionTimeoutState();
    dispatch(hideSessionTimeout());
    dispatch(hideSessionWarning());
    dispatch(setLoading(false));
  }, [dispatch]);

  // TC-AUTH-008: Check for concurrent session on mount
  useEffect(() => {
    const checkConcurrentSession = () => {
      try {
        const isConcurrentSession = localStorage.getItem('concurrentSessionDetected') === 'true';
        const message = localStorage.getItem('concurrentSessionMessage') || 'Logged in from another device. Please log in again.';
        
        if (isConcurrentSession) {
          setConcurrentSessionMessage(message);
          // Clear the flags so the message only shows once
          localStorage.removeItem('concurrentSessionDetected');
          localStorage.removeItem('concurrentSessionMessage');
        }
      } catch (error) {
        console.error('Error checking concurrent session:', error);
      }
    };

    checkConcurrentSession();
  }, []);

  // Lock userType to 'admin' when bypass is verified
  useEffect(() => {
    if (bypassVerified) {
      setUserType('admin');
    }
  }, [bypassVerified]);

  // Auto-refresh maintenance status every 30 seconds when in maintenance mode
  useEffect(() => {
    if (!isMaintenanceMode) return;

    const interval = setInterval(() => {
      checkMaintenanceStatus();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isMaintenanceMode]);

  const checkMaintenanceStatus = async () => {
    setIsChecking(true);
    try {
      const response = await apiService.getMaintenanceStatus();
      setLastChecked(new Date());
      
      // If maintenance is disabled, reload the page to allow normal access
      if (response.data?.data && !response.data.data.enabled) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error checking maintenance status:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEstimatedTime = () => {
    if (maintenanceData?.enabledAt && maintenanceData?.estimatedDuration) {
      const startTime = new Date(maintenanceData.enabledAt);
      const estimated = new Date(startTime.getTime() + maintenanceData.estimatedDuration * 60000);
      return estimated.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return null;
  };

  const handleBypassSubmit = async (e) => {
    e.preventDefault();
    if (!bypassToken.trim()) return;

    setIsVerifying(true);
    setBypassError(null);

    try {
      // Call API to verify bypass token
      const response = await apiService.api.post('/system/maintenance/verify-bypass', {
        bypassToken: bypassToken.trim()
      });

      if (response.data?.status === 'success') {
        // Token is valid - server sets httpOnly bypass cookie
        setBypassVerified(true);
        setBypassError(null);
      } else {
        setBypassError('Invalid bypass token. Please check and try again.');
      }
    } catch (error) {
      console.error('Bypass token verification error:', error);
      setBypassError(
        error.response?.data?.message || 
        'Failed to verify bypass token. Please try again.'
      );
    } finally {
      setIsVerifying(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      // Prefer this tab's own pre-timeout page (sessionStorage, per-tab) over the shared localStorage
      // copy, so each tab restores to ITS OWN page after a cross-tab re-login.
      const storedRedirectUrl = getPerTabRestorePath() || getRedirectAfterLogin();
      
      let from;
      if (storedRedirectUrl && storedRedirectUrl !== '/login' && !storedRedirectUrl.startsWith('/login')) {
        // Use the stored redirect URL from session timeout
        from = storedRedirectUrl;
        // Clear the stored URL after using it
        clearPerTabRestorePath();
        clearRedirectAfterLogin();
      } else {
        // Use default redirect (from location state or dashboard)
        from = location.state?.from?.pathname || '/dashboard';
      }
      from = normalizeLoginRedirect(from, user.role);
      
      // Preserve bypass token in URL if present
      const searchParams = new URLSearchParams(location.search);
      const bypassToken = searchParams.get('bypassToken');
      
      // Build redirect URL with bypass token if present
      let redirectUrl;
      if (bypassToken && !from.includes('bypassToken')) {
        const separator = from.includes('?') ? '&' : '?';
        redirectUrl = `${from}${separator}bypassToken=${encodeURIComponent(bypassToken)}`;
      } else {
        redirectUrl = from;
      }
      
      navigate(redirectUrl, { replace: true });
    }
  }, [user, navigate, location]);

  // Cross-tab re-login restoration: when another tab logs in (the shared session cookie becomes
  // valid), re-authenticate THIS still-logged-out tab so the effect above sends it back to its page.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'user' && event.newValue && !user) {
        dispatch(verifyAuth());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user, dispatch]);

  // Validation function
  const validateForm = () => {
    const errors = {
      username: '',
      password: '',
      companyCode: ''
    };
    let isValid = true;

    // Validate username
    if (!username.trim()) {
      errors.username = 'Username is required';
      isValid = false;
    }

    // Validate password
    if (!password.trim()) {
      errors.password = 'Password is required';
      isValid = false;
    }

    // Validate company code for agent type
    if (userType === 'agent' && !bypassVerified && !companyCode.trim()) {
      errors.companyCode = 'Company code is required';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Handle field blur for validation
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Validate single field
    const errors = { ...validationErrors };
    
    if (field === 'username' && !username.trim()) {
      errors.username = 'Username is required';
    } else if (field === 'username') {
      errors.username = '';
    }
    
    if (field === 'password' && !password.trim()) {
      errors.password = 'Password is required';
    } else if (field === 'password') {
      errors.password = '';
    }
    
    if (field === 'companyCode' && userType === 'agent' && !bypassVerified && !companyCode.trim()) {
      errors.companyCode = 'Company code is required';
    } else if (field === 'companyCode') {
      errors.companyCode = '';
    }
    
    setValidationErrors(errors);
  };

  // Handle field change with validation clear
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (touched.username && e.target.value.trim()) {
      setValidationErrors(prev => ({ ...prev, username: '' }));
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (touched.password && e.target.value.trim()) {
      setValidationErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleCompanyCodeChange = (e) => {
    setCompanyCode(e.target.value);
    if (touched.companyCode && e.target.value.trim()) {
      setValidationErrors(prev => ({ ...prev, companyCode: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      username: true,
      password: true,
      companyCode: true
    });

    // Validate form
    if (!validateForm()) {
      // Form validation failed - prevent submission
      return;
    }

    // Get bypass token from URL if present
    const searchParams = new URLSearchParams(location.search);
    const bypassToken = searchParams.get('bypassToken');

    // Include company code only if user type is agent
    const loginData = {
      username,
      password,
      ...(userType === 'agent' && { companyCode })
    };

    try {
      clearSessionTimeoutState();
      dispatch(hideSessionTimeout());
      dispatch(hideSessionWarning());
      const result = await dispatch(loginUser({ 
        loginData, 
        bypassToken 
      })).unwrap();

      if (result) {
        // Prefer this tab's own pre-timeout page (per-tab) over the shared localStorage copy.
        const storedRedirectUrl = getPerTabRestorePath() || getRedirectAfterLogin();
        
        let from;
        if (storedRedirectUrl && storedRedirectUrl !== '/login' && !storedRedirectUrl.startsWith('/login')) {
          // Use the stored redirect URL from session timeout
          from = storedRedirectUrl;
          // Clear the stored URL after using it
          clearPerTabRestorePath();
          clearRedirectAfterLogin();
        } else {
          // Use default redirect (from location state or dashboard)
          from = location.state?.from?.pathname || '/dashboard';
        }
        from = normalizeLoginRedirect(from, result.user?.role || result.role);
        
        // Preserve bypass token in URL if present
        const bypassToken = searchParams.get('bypassToken');
        
        // Build redirect URL with bypass token if present
        let redirectUrl;
        if (bypassToken && !from.includes('bypassToken')) {
          const separator = from.includes('?') ? '&' : '?';
          redirectUrl = `${from}${separator}bypassToken=${encodeURIComponent(bypassToken)}`;
        } else {
          redirectUrl = from;
        }
        
        navigate(redirectUrl, { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);
      // Error is handled by Redux state
      // Clear password field on login failure (username is preserved)
      setPassword('');
      // Log error to console for debugging
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand Section */}
      <div className="hidden lg:flex lg:w-1/2 p-9 flex-col justify-between relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700">
        {/* Decorative circles */}
        <div className="absolute -top-32 -right-32 w-64 h-64 bg-white opacity-5 rounded-full"></div>
        <div className="absolute -bottom-48 -left-48 w-96 h-96 bg-white opacity-5 rounded-full"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-blue-500 font-bold text-base">
              TMS
            </div>
            <span className="text-white text-xl font-semibold">TaxManage</span>
          </div>
          
          <div className="mt-12">
            <h1 className="text-white text-3xl font-bold mb-3 leading-tight">
              {isMaintenanceMode ? 'System Under Maintenance' : (
                <>
                  Streamline Your<br />Tax Management
                </>
              )}
            </h1>
            <p className="text-white text-base opacity-90">
              {isMaintenanceMode 
                ? 'We are currently performing system updates. Please check back shortly.' 
                : 'Powerful tools for tax professionals and insurance agents'}
            </p>
          </div>
        </div>
        
        <div className="relative z-10 text-white text-xs opacity-75">
          © 2025 TaxManage System. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Login Form or Maintenance Card */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2.5 mb-6">
            <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center text-white text-base font-semibold">
              TMS
            </div>
            <span className="text-gray-800 text-lg font-medium">TaxManage</span>
          </div>

          {/* Maintenance Mode - Full Card Display */}
          {isMaintenanceMode && !bypassVerified ? (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 bg-amber-100 rounded-full flex items-center justify-center">
                  <ToolOutlined className="text-xl text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">System Under Maintenance</h3>
                  <p className="text-gray-500 text-sm">Please check back shortly</p>
                </div>
              </div>

              {/* Maintenance Details */}
              <div className="space-y-3 mb-5">
                {maintenanceData?.reason && (
                  <div className="text-xs text-amber-800 bg-amber-50 rounded-lg p-3 border border-amber-200">
                    <span className="font-medium text-amber-900">Reason:</span> {maintenanceData.reason}
                  </div>
                )}
                
                {getEstimatedTime() && (
                  <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <ClockCircleOutlined className="text-blue-500" />
                    <span><span className="font-medium">Estimated completion:</span> {getEstimatedTime()}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <ReloadOutlined className={isChecking ? 'animate-spin' : ''} />
                  <span>Last checked: {formatTime(lastChecked)}</span>
                </div>
              </div>

              {/* Manual Refresh Button */}
              <Button
                type="primary"
                onClick={checkMaintenanceStatus}
                loading={isChecking}
                icon={<ReloadOutlined className={isChecking ? 'animate-spin' : ''} />}
                block
                className="mb-5"
              >
                {isChecking ? 'Checking...' : 'Check Again'}
              </Button>

              {/* Admin Bypass Section */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-xs text-gray-500 mb-3 text-center">
                  Need urgent access? Contact your system administrator
                </p>
                
                {/* Bypass Error Message */}
                {bypassError && (
                  <Alert
                    message={bypassError}
                    type="error"
                    showIcon
                    style={{ marginBottom: '12px', borderRadius: '8px' }}
                  />
                )}
                
                {!showBypassInput ? (
                  <Button
                    type="default"
                    onClick={() => setShowBypassInput(true)}
                    icon={<KeyOutlined />}
                    block
                  >
                    Admin Access
                  </Button>
                ) : (
                  <form onSubmit={handleBypassSubmit} className="space-y-3">
                    <div className="relative">
                      <KeyOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="text"
                        value={bypassToken}
                        onChange={(e) => setBypassToken(e.target.value)}
                        placeholder="Enter admin bypass token"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        autoFocus
                        disabled={isVerifying}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="primary"
                        htmlType="submit"
                        disabled={!bypassToken.trim() || isVerifying}
                        loading={isVerifying}
                        className="flex-1"
                      >
                        {isVerifying ? 'Verifying...' : 'Verify & Access'}
                      </Button>
                      <Button
                        type="default"
                        onClick={() => {
                          setShowBypassInput(false);
                          setBypassToken('');
                          setBypassError(null);
                        }}
                        disabled={isVerifying}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </>
          ) : (
            /* Normal Login Form - Admin Only when bypass verified */
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-1.5 leading-tight">
                  {bypassVerified ? 'Admin Access' : 'Welcome back'}
                </h2>
                <p className="text-gray-500 text-sm">
                  {bypassVerified 
                    ? 'System is under maintenance. Admin login only.' 
                    : 'Sign in to access your dashboard'}
                </p>
              </div>

              {/* Maintenance bypass notice */}
              {bypassVerified && (
                <Alert
                  message="Maintenance Mode - Admin Access Only"
                  description="You have bypassed maintenance mode. Only admin login is available."
                  type="warning"
                  showIcon
                  icon={<ToolOutlined />}
                  style={{ marginBottom: '18px', borderRadius: '8px' }}
                />
              )}

              {/* TC-AUTH-008: Concurrent session message */}
              {concurrentSessionMessage && (
                <Alert
                  message={concurrentSessionMessage}
                  type="warning"
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  style={{ marginBottom: '18px', borderRadius: '8px' }}
                  closable
                  onClose={() => setConcurrentSessionMessage(null)}
                />
              )}

              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  style={{ marginBottom: '18px', borderRadius: '8px' }}
                />
              )}

              <div>
                {/* User Type Selector - Hidden when bypass verified */}
                {!bypassVerified && (
                  <div className="mb-5">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => setUserType('admin')}
                        type={userType === 'admin' ? 'primary' : 'default'}
                        block
                        className="h-9 rounded-full"
                      >
                        Admin
                      </Button>
                      <Button
                        onClick={() => setUserType('agent')}
                        type={userType === 'agent' ? 'primary' : 'default'}
                        block
                        className="h-9 rounded-full"
                      >
                        Insurance Agent
                      </Button>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block mb-1.5 text-gray-600 text-xs">
                    Username
                  </label>
                  <div className="relative">
                    <UserOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-sm" />
                    <input
                      type="text"
                      value={username}
                      onChange={handleUsernameChange}
                      onBlur={() => handleBlur('username')}
                      placeholder="Enter your username"
                      className={`w-full h-9 pl-9 pr-3 rounded-full border text-xs outline-none transition-all bg-white focus:border-blue-500 ${
                        touched.username && validationErrors.username 
                          ? 'border-red-500 focus:border-red-500' 
                          : 'border-gray-200'
                      }`}
                    />
                  </div>
                  {touched.username && validationErrors.username && (
                    <p className="mt-1 text-xs text-red-500">{validationErrors.username}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block mb-1.5 text-gray-600 text-xs">
                    Password
                  </label>
                  <div className="relative">
                    <LockOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-sm" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      onBlur={() => handleBlur('password')}
                      placeholder="Enter your password"
                      className={`w-full h-9 pl-9 pr-9 rounded-full border text-xs outline-none transition-all bg-white focus:border-blue-500 ${
                        touched.password && validationErrors.password 
                          ? 'border-red-500 focus:border-red-500' 
                          : 'border-gray-200'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500 transition-colors bg-transparent border-none cursor-pointer p-0"
                    >
                      {showPassword ? (
                        <EyeInvisibleOutlined className="text-sm" />
                      ) : (
                        <EyeOutlined className="text-sm" />
                      )}
                    </button>
                  </div>
                  {touched.password && validationErrors.password && (
                    <p className="mt-1 text-xs text-red-500">{validationErrors.password}</p>
                  )}
                </div>

                {/* Company Code - Only show for agent type AND when not bypass verified */}
                {userType === 'agent' && !bypassVerified && (
                  <div className="mb-4">
                    <label className="block mb-1.5 text-gray-600 text-xs">
                      Company Code
                    </label>
                    <div className="relative">
                      <BankOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-sm" />
                      <input
                        type="text"
                        value={companyCode}
                        onChange={handleCompanyCodeChange}
                        onBlur={() => handleBlur('companyCode')}
                        placeholder="Enter your company code"
                        className={`w-full h-9 pl-9 pr-3 rounded-full border text-xs outline-none transition-all bg-white focus:border-blue-500 ${
                          touched.companyCode && validationErrors.companyCode 
                            ? 'border-red-500 focus:border-red-500' 
                            : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {touched.companyCode && validationErrors.companyCode && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.companyCode}</p>
                    )}
                  </div>
                )}

                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={loading}
                  block
                  className="h-9 rounded-full mt-5"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </div>
            </>
          )}

          {/* Mobile Copyright */}
          <div className="lg:hidden text-center mt-6 text-gray-500 text-xs">
            © 2025 TaxManage System. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
